import { useFlowDownload } from "../hooks/useFlowDownload";
import type { MigrationResult } from "../types";

interface Props {
  running: boolean;
  botName: string | null;
  planner: string;
  result: MigrationResult | null;
  error: string | null;
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

const Ids = ({ ids }: { ids: string[] }) => (
  <>
    {ids.map((id) => (
      <code key={id}>{id}</code>
    ))}
  </>
);

export function ResultPanel({ running, botName, planner, result, error }: Props) {
  // The run's own folder, which is unique per migration. Using the bot name
  // would read whichever run happened to be last.
  const flows = useFlowDownload(result?.artifactFolder ?? result?.botName ?? botName);
  const flowIds = result?.created?.flows ?? [];

  if (running) {
    return (
      <p className="running">
        Migrating <strong>{botName}</strong> with the {planner} planner&hellip;
      </p>
    );
  }

  if (error) return <p className="error">{error}</p>;
  if (!result) return null;

  const { created, deployment, contactFlow } = result;

  // What the run actually achieved, in the order someone would say it aloud.
  const headline = [
    created?.flows?.length ? `${created.flows.length} flows` : null,
    created?.slotTypes?.length ? `${created.slotTypes.length} slot types` : null,
    deployment?.ok ? `deployed to ${deployment.environment}` : null,
    contactFlow?.ok ? "contact flow ready" : null,
    contactFlow?.phoneNumber?.ok
      ? `live on ${contactFlow.phoneNumber.phoneNumber}`
      : null,
  ].filter(Boolean);

  return (
    <>
      <div className={`result-banner ${result.ok ? "ok" : "bad"}`}>
        <span className="result-icon" aria-hidden="true">
          {result.ok ? "✓" : "!"}
        </span>
        <span className="result-text">
          <span className="result-title">
            {result.ok
              ? `${result.botName ?? botName} migrated`
              : "Finished with problems"}
          </span>
          <span className="result-sub">
            {result.ok
              ? headline.join(" · ") || `via the ${result.planner} planner`
              : result.error}
          </span>
        </span>
      </div>

      {/* Each of these can fail after everything before it succeeded, so they
          report on their own rather than flipping the whole result. */}
      {deployment?.error && <p className="error">{deployment.error}</p>}
      {contactFlow?.error && <p className="error">{contactFlow.error}</p>}

      <dl className="kv">
        {result.applicationId && (
          <Row label="Application">
            <code>{result.applicationId}</code>
          </Row>
        )}

        {created?.flows?.length ? (
          <Row label="Flows">
            <Ids ids={created.flows} />
          </Row>
        ) : null}
        {created?.slotTypes?.length ? (
          <Row label="Slot types">
            <Ids ids={created.slotTypes} />
          </Row>
        ) : null}
        {result.contactFlow?.phoneNumber && (
          <Row label="Phone number">
            {result.contactFlow.phoneNumber.ok ? (
              <>
                <code className="phone">
                  {result.contactFlow.phoneNumber.phoneNumber}
                </code>
                <span className="count">answers with this flow</span>
              </>
            ) : (
              <span className="phone-problem">
                <code>{result.contactFlow.phoneNumber.phoneNumber}</code>{" "}
                not attached &mdash; {result.contactFlow.phoneNumber.error}
              </span>
            )}
          </Row>
        )}

        {result.contactFlow?.contactFlowId && (
          <Row label="Contact flow">
            <code>{result.contactFlow.name}</code>
            <span className="count">{result.contactFlow.operation}</span>
          </Row>
        )}
      </dl>

      {result.applicationUrl && (
        <p className="cta-row">
          {/* noreferrer as well as noopener: the referrer would otherwise tell
              CX Designer's logs which bot was just migrated. */}
          <a
            className="cta"
            href={result.applicationUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open in CX Designer &rarr;
          </a>
        </p>
      )}

      {flowIds.length > 0 && (
        <div className="downloads">
          <div className="downloads-head">
            <span className="downloads-title">Flow definitions</span>
            <button
              type="button"
              className="ghost"
              disabled={flows.busy !== null}
              onClick={() => void flows.downloadAll(flowIds)}
            >
              {flows.busy === "__all__"
                ? "Preparing…"
                : `Download all (${flowIds.length})`}
            </button>
          </div>

          <ul className="download-list">
            {flowIds.map((flowId) => (
              <li key={flowId}>
                <code>{flowId}</code>
                <button
                  type="button"
                  className="ghost small"
                  disabled={flows.busy !== null}
                  onClick={() => void flows.downloadOne(flowId)}
                >
                  {flows.busy === flowId ? "…" : "JSON"}
                </button>
              </li>
            ))}
          </ul>

        </div>
      )}
      {flows.error && <p className="error">{flows.error}</p>}
    </>
  );
}
